import {
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {ReplaySubject, Subject} from 'rxjs';
import {MatSelect} from '@angular/material/select';
import {NgForOfContext} from '@angular/common';
import {MatOption} from '@angular/material/core';

export interface PortalSelectSearchEvent {
  value: string;
}

@Component({
  selector: 'app-portal-select',
  templateUrl: './portal-select.component.html',
  styleUrls: ['./portal-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PortalSelectComponent),
    multi: true
  }]
})
export class PortalSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {

  public searchFormControl: FormControl = new FormControl();
  public selectionFormControl: FormControl = new FormControl();
  @ContentChild('option', {static: false}) option: TemplateRef<NgForOfContext<any, [any]>>;
  @ViewChild('select') select: MatSelect;
  @ViewChild('allMatOption', {static: false}) allMatOption: MatOption;
  @Output() filter: EventEmitter<PortalSelectSearchEvent> = new EventEmitter<PortalSelectSearchEvent>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() blurEvent = new EventEmitter<any>();
  @Output() onSelectionChange = new EventEmitter<any>();
  @Input() placeholder: string;
  @Input() searchPlaceHolder: string;
  @Input() noEntriesFoundLabel: string;
  @Input() withFilter: boolean;
  @Input() clearable: boolean;
  @Input() multiple: boolean;
  @Input() value: any;
  totalItems: any[];
  @Input() itemId: any;
  @Input() itemName: any;
  @Input() filteredItems: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  @Input() prefix: any;
  @Input() allOption = false;
  @Input() showMessage = true;
  @Input() disableSelectedItems = false;
  @Input() hideSelectedItems = false;
  private onDestroy = new Subject<void>();
  private onChange: (_: any) => void;
  private onTouched: () => void;
  @Input() selectedItems: any[];

  constructor() {
    this.onChange = (newValue: any) => {
      this.value = newValue;
    };
    this.onTouched = () => {
    };
    this.disabled = false;
  }

  @Input() set items(items: any[]) {
    if (items) {
      this.totalItems = items;
    } else {
      this.totalItems = [];
    }
    this.loadItems();
  }

  @Input() set matValue(value: boolean) {
    this.selectionFormControl.setValue(value);
  }

  @Input() set disabled(value: boolean) {
    if (value === true) {
      this.selectionFormControl.disable();
      this.clearable = false;
    } else {
      this.selectionFormControl.enable();
    }
  }

  @Input() set required(value: boolean) {
    if (value === true) {
      this.selectionFormControl.setValidators(Validators.compose([Validators.required]));
    } else {
      this.selectionFormControl.clearValidators();
    }
  }

  @Input() optionDisabled(item: any) {
    if (this.disableSelectedItems) {
      return this.selectedItems?.includes(item.id);
    }
  }

  @Input() optionHidden(item: any) {
    if (this.hideSelectedItems) {
      return this.selectedItems?.includes(item.id);
    }
  }

  ngOnInit(): void {
    this.searchFormControl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(newValue => {
        this.filterItems();
      });
    this.selectionFormControl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(newValue => {
        this.value = newValue;
      });
    this.loadItems();
  }

  checkIfClearable() {
    const selectionValue = this.selectionFormControl.value;
    if ((selectionValue || selectionValue === false) && this.clearable) {
      if (typeof (selectionValue) === 'object') {
        if (selectionValue.length === 0) {
          return false;
        }
      } else if (typeof (selectionValue) === 'function') {
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

  loadItems() {
    if (this.totalItems) {
      this.filteredItems.next(this.totalItems.slice());
    }

    if (this.allOption && this.value) {
      if (this.value.length > 0) {
        if (this.value.length === this.totalItems.length) {
          this.allMatOption.select();
          this.selectAll(null);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    if (fn) {
      this.selectionFormControl.valueChanges
        .subscribe(fn);
    }
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
    this.selectionFormControl.setValue(obj);
  }

  clearValue() {
    this.writeValue(null);
    this.selectionChange.emit({value: null});
    this.onSelectionChange.emit({source: {value: null}});
  }

  public selectAll(event) {
    if (this.allOption && event?.isUserInput) {
      if (this.allMatOption.selected) {
        this.selectionFormControl.setValue([...this.totalItems.map(it => it[this.itemId]), 0]);
      } else {
        this.selectionFormControl.setValue([], {emitEvent: true});
      }
    }
  }

  public selectOne() {
    if (this.allOption) {
      if (Array.isArray(this.value) && this.allMatOption.selected) {
        this.allMatOption.deselect();
        return;
      }
      if (this.selectionFormControl.value.length === this.totalItems) {
        this.allMatOption.select();
      }
      if (this.value) {
        if (this.value.length > 0) {
          if (this.value.length === this.totalItems.length) {
            this.allMatOption.select();
            this.selectAll(null);
          }
        }
      }
    }
  }

  protected filterItems() {
    if (!this.totalItems) {
      return;
    }
    let search = this.searchFormControl.value;
    if (!search) {
      this.filteredItems.next(this.totalItems.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    if (this.filter.observers.length > 0) {
      this.filter.emit(search);
    } else {
      this.filteredItems.next(this.totalItems.filter(item => (item[this.itemName] ? ((this.itemId ? item[this.itemId] : '')
        + ' ' + item[this.itemName]).toLowerCase().indexOf(search) : (item.name ? item.name.toLowerCase().indexOf(search) : ''.toLowerCase().indexOf(search))) > -1));
    }
  }
}
